export const NAV_CHROME_SNAPSHOT_KEY = 'bz-nav-chrome-snapshot';
export const NAV_CHROME_LOGOUT_KEY = 'bz-nav-logout';
export const NAV_CHROME_USER_ATTR = 'data-nav-user';
export const NAV_CHROME_READY_ATTR = 'data-nav-ready';

export type NavChromeUser = 'anon' | 'free' | 'edge' | 'pro';

export interface NavChromeServices {
  edge: boolean;
  pro: boolean;
  research: boolean;
}

const EMPTY_SERVICES: NavChromeServices = { edge: false, pro: false, research: false };

const SERVICE_ATTRS = [
  ['data-nav-edge', 'edge'],
  ['data-nav-pro', 'pro'],
  ['data-nav-research', 'research'],
] as const;

export const isLoggedInFromAccessType = (accessType: string | undefined): boolean =>
  Boolean(accessType) && accessType !== 'anonymous';

export const readAccessTypeFromCookieString = (cookieHeader: string): string | undefined => {
  const cookieMatch = cookieHeader.match(/(?:^|; )bz_access_type=([^;]*)/);
  if (!cookieMatch) {
    return undefined;
  }
  try {
    return decodeURIComponent(cookieMatch[1]);
  } catch {
    return cookieMatch[1];
  }
};

export const parseNavChromeSnapshotUser = (raw: string | null): Exclude<NavChromeUser, 'anon'> | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const snapshot = parsed as Record<string, unknown>;
    if (snapshot.version !== 1 && snapshot.version !== 2) {
      return null;
    }
    if (snapshot.user === 'free' || snapshot.user === 'edge' || snapshot.user === 'pro') {
      return snapshot.user;
    }
    return null;
  } catch {
    return null;
  }
};

export const resolveNavChromeUser = (
  isLoggedIn: boolean,
  access: { hasEdge: boolean; hasPro: boolean },
): NavChromeUser => {
  if (!isLoggedIn) {
    return 'anon';
  }
  if (access.hasPro) {
    return 'pro';
  }
  if (access.hasEdge) {
    return 'edge';
  }
  return 'free';
};

export const resolveNavChromeUserFromCookieAndSnapshot = (
  cookieHeader: string,
  snapshotRaw: string | null,
): NavChromeUser => {
  if (!isLoggedInFromAccessType(readAccessTypeFromCookieString(cookieHeader))) {
    return 'anon';
  }
  return parseNavChromeSnapshotUser(snapshotRaw) ?? 'free';
};

export const parseNavChromeServices = (raw: string | null): NavChromeServices => {
  const user = parseNavChromeSnapshotUser(raw);
  if (!user || !raw) {
    return EMPTY_SERVICES;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || (parsed as Record<string, unknown>).version !== 2) {
      return EMPTY_SERVICES;
    }
    const snapshot = parsed as Record<string, unknown>;
    return {
      edge: snapshot.edge === true,
      pro: snapshot.pro === true,
      research: snapshot.research === true,
    };
  } catch {
    return EMPTY_SERVICES;
  }
};

export const applyNavChromeDocumentAttributes = (
  user: NavChromeUser,
  services: NavChromeServices,
  ready: boolean,
): void => {
  const root = document.documentElement;
  root.setAttribute(NAV_CHROME_USER_ATTR, user);
  for (const [attribute, key] of SERVICE_ATTRS) {
    if (user !== 'anon' && services[key]) {
      root.setAttribute(attribute, '');
    } else {
      root.removeAttribute(attribute);
    }
  }
  if (ready) {
    root.setAttribute(NAV_CHROME_READY_ATTR, '');
  } else {
    root.removeAttribute(NAV_CHROME_READY_ATTR);
  }
};

export const writeNavChromeSnapshot = (user: NavChromeUser, services: NavChromeServices): void => {
  try {
    if (user === 'anon' || sessionStorage.getItem(NAV_CHROME_LOGOUT_KEY) === '1') {
      localStorage.removeItem(NAV_CHROME_SNAPSHOT_KEY);
      return;
    }
    localStorage.setItem(
      NAV_CHROME_SNAPSHOT_KEY,
      JSON.stringify({ version: 2, user, edge: services.edge, pro: services.pro, research: services.research }),
    );
  } catch {
    return;
  }
};

export const isNavChromeLogoutPending = (): boolean => {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(NAV_CHROME_LOGOUT_KEY) === '1';
  } catch {
    return false;
  }
};

export const clearNavChromeForLogout = (): void => {
  try {
    sessionStorage.setItem(NAV_CHROME_LOGOUT_KEY, '1');
    localStorage.removeItem(NAV_CHROME_SNAPSHOT_KEY);
  } catch {
    return;
  }
  applyNavChromeDocumentAttributes('anon', EMPTY_SERVICES, true);
};

export const NAV_CHROME_CRITICAL_CSS = `html:not([${NAV_CHROME_READY_ATTR}]) [data-nav-boot="authed"]{display:none!important}html:not([${NAV_CHROME_READY_ATTR}])[${NAV_CHROME_USER_ATTR}="free"] [data-nav-boot="anon"],html:not([${NAV_CHROME_READY_ATTR}])[${NAV_CHROME_USER_ATTR}="edge"] [data-nav-boot="anon"],html:not([${NAV_CHROME_READY_ATTR}])[${NAV_CHROME_USER_ATTR}="pro"] [data-nav-boot="anon"]{display:none!important}html:not([${NAV_CHROME_READY_ATTR}])[${NAV_CHROME_USER_ATTR}="free"] [data-nav-boot="authed"],html:not([${NAV_CHROME_READY_ATTR}])[${NAV_CHROME_USER_ATTR}="edge"] [data-nav-boot="authed"],html:not([${NAV_CHROME_READY_ATTR}])[${NAV_CHROME_USER_ATTR}="pro"] [data-nav-boot="authed"]{display:flex!important}@media(min-width:981px){html:not([${NAV_CHROME_READY_ATTR}]) [data-nav-boot="edge"]{display:none!important}html:not([${NAV_CHROME_READY_ATTR}])[${NAV_CHROME_USER_ATTR}="edge"] [data-nav-boot="edge"]{display:inline-flex!important}html:not([${NAV_CHROME_READY_ATTR}])[${NAV_CHROME_USER_ATTR}="edge"] [data-nav-boot="pro"]{display:none!important}}html:not([${NAV_CHROME_READY_ATTR}]) [data-nav-boot^="svc"]{display:none!important}html:not([${NAV_CHROME_READY_ATTR}])[data-nav-edge] [data-nav-boot="svc-edge"],html:not([${NAV_CHROME_READY_ATTR}])[data-nav-pro] [data-nav-boot="svc-pro"],html:not([${NAV_CHROME_READY_ATTR}])[data-nav-research] [data-nav-boot="svc-research"]{display:block!important}html:not([${NAV_CHROME_READY_ATTR}])[data-nav-edge] [data-nav-boot="svc-divider"],html:not([${NAV_CHROME_READY_ATTR}])[data-nav-pro] [data-nav-boot="svc-divider"],html:not([${NAV_CHROME_READY_ATTR}])[data-nav-research] [data-nav-boot="svc-divider"]{display:block!important}`;

export const NAV_CHROME_BOOT_SCRIPT = `(function(){var r=document.documentElement,c=document.cookie||'',m=c.match(/(?:^|; )bz_access_type=([^;]*)/),a='',loggedOut=false;if(m){try{a=decodeURIComponent(m[1])}catch(e){a=m[1]}}try{loggedOut=sessionStorage.getItem('${NAV_CHROME_LOGOUT_KEY}')==='1'}catch(e){}var user='anon';if(!loggedOut&&a&&a!=='anonymous'){user='free';try{var s=localStorage.getItem('${NAV_CHROME_SNAPSHOT_KEY}');if(s){var p=JSON.parse(s);if(p&&(p.version===1||p.version===2)&&(p.user==='free'||p.user==='edge'||p.user==='pro')){user=p.user;if(p.version===2){if(p.edge)r.setAttribute('data-nav-edge','');if(p.pro)r.setAttribute('data-nav-pro','');if(p.research)r.setAttribute('data-nav-research','')}}}}catch(e){}}else{try{localStorage.removeItem('${NAV_CHROME_SNAPSHOT_KEY}');if(!a||a==='anonymous')sessionStorage.removeItem('${NAV_CHROME_LOGOUT_KEY}')}catch(e){}}r.setAttribute('${NAV_CHROME_USER_ATTR}',user)})();`;
